import MESSAGES from 'constants/messages';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import downloadFile from 'store/utils/downloadFile';
import { getCurrentPage } from 'store/utils/pagination';

import {
  calculateNewFilterOption,
  calculateFinalParams,
} from 'store/utils/handleParams';
import _isEmpty from 'lodash/isEmpty';
import * as api from './api';
import * as types from './actionTypes';
import * as dictionaryColsTypes from '../dictionaryColumns/actionTypes';
import * as selectors from './selectors';

/**
 * Check if a dictionary already exists
 *
 * @param fieldName
 * @param fieldValue
 * @param exception
 * @returns {Function}
 */
export const checkDictionaryName = (
  fieldName,
  fieldValue,
  exception = null,
) => async () => {
  try {
    const { data } = await api.checkDictionaryNameRequest(
      fieldValue,
      exception,
    );
    if (data.count > 0) {
      // eslint-disable-next-line no-throw-literal
      throw {
        [fieldName]: MESSAGES.DICTIONARIES.NAME.EXISTS,
      };
    }
  } catch (err) {
    throw err;
  }
};

/**
 * Get All entities
 *
 * @param params
 */
export const getAllDictionaries = (params = {}) => async dispatch => {
  try {
    dispatch({
      type: types.GET_DICTIONARY_ALL_REQUEST,
    });
    const { data } = await api.getDictionariesRequest(params);
    dispatch({
      type: types.GET_DICTIONARY_ALL_SUCCESS,
      payload: {
        items: data.results,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_DICTIONARY_ALL_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Get paginated dictionaries
 *
 * @param paginationParams
 * @param filterParams
 */
export const getDictionariesList = (
  paginationParams = {},
  filterParams = {},
) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_DICTIONARY_LIST_REQUEST,
    });

    const state = getState();
    let filterOpt = filterParams;

    if (_isEmpty(filterOpt)) {
      filterOpt = selectors.getFilterParams(state);
    }

    const paginationOptions = {
      ...selectors.getPaginationOptions(state),
      ...paginationParams,
    };

    const filterOptions = {
      ...selectors.getFilterParams(state),
      ...filterOpt,
    };

    const finalParams = calculateFinalParams(filterOptions, paginationOptions);

    const { data } = await api.getDictionariesRequest(finalParams);

    await dispatch({
      type: types.UPDATE_DICTIONARY_LIST_OPTIONS,
      payload: {
        paginationOptions,
        filterOptions: calculateNewFilterOption(
          selectors.getFilterOptions(state),
          filterOpt,
        ),
        filterParams: filterOptions,
      },
    });

    dispatch({
      type: types.GET_DICTIONARY_LIST_SUCCESS,
      payload: {
        items: data.results,
        count: data.count,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_DICTIONARY_LIST_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add a dictionary
 *
 * @param data
 */
export const addDictionary = data => async dispatch => {
  try {
    await dispatch({
      type: types.ADD_DICTIONARY_REQUEST,
    });

    const response = await api.addDictionaryRequest(data);

    await dispatch({
      type: types.ADD_DICTIONARY_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(getDictionariesList({ page: 1 }));

    Notify.success({
      title: 'Dictionary was created successfully',
    });

    // FIXME: call selectContext instead of? Check how it works with routing.
    return response.data.id;
  } catch (error) {
    await dispatch({
      type: types.ADD_DICTIONARY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to create Dictionary',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Get dictionary
 *
 * @param id
 * @param params
 * @returns {Function}
 */
export const getDictionary = (id, params = {}) => async dispatch => {
  try {
    dispatch({
      type: types.GET_DICTIONARY_REQUEST,
    });

    const { data } = await api.getDictionaryRequest(id, params);

    dispatch({
      type: types.GET_DICTIONARY_SUCCESS,
      payload: {
        item: data,
      },
    });

    // TODO: Define action for this in `dictionaryColumns`, use it instead of.
    dispatch({
      type: dictionaryColsTypes.GET_DICTIONARY_COLUMNS_SUCCESS,
      payload: {
        dictionaryColumns: data.columns,
        count: data.columns.length,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_DICTIONARY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update dictionary
 *
 * @param data
 * @returns {Function}
 */
export const updateDictionary = data => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.UPDATE_DICTIONARY_REQUEST,
    });

    const dictionaryId = selectors.getDictionary(getState()).id;
    const response = await api.updateDictionaryRequest(dictionaryId, data);
    dispatch({
      type: types.UPDATE_DICTIONARY_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    dispatch(getDictionariesList(selectors.getPaginationOptions(getState())));

    Notify.success({
      title: 'Dictionary was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_DICTIONARY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Dictionary',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Delete a dictionary
 *
 * @param id
 */
export const deleteDictionary = id => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.DELETE_DICTIONARY_REQUEST,
    });

    await api.deleteDictionaryRequest(id);

    dispatch({
      type: types.DELETE_DICTIONARY_SUCCESS,
    });

    await dispatch(
      getDictionariesList({
        page: getCurrentPage(
          selectors.getPaginationOptions(getState()).page,
          selectors.getDictionariesList(getState()),
        ),
      }),
    );

    Notify.success({
      title: 'Dictionary was deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_DICTIONARY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Dictionary',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Export dictionary
 *
 * @param dictionary
 */
export const exportDictionary = dictionary => async dispatch => {
  try {
    dispatch({
      type: types.EXPORT_DICTIONARY_REQUEST,
    });

    const { data } = await api.exportDictionaryRequest(dictionary.id);

    await dispatch({
      type: types.EXPORT_DICTIONARY_SUCCESS,
    });

    await downloadFile(data, `${dictionary.name}.csv`);
  } catch (error) {
    dispatch({
      type: types.EXPORT_DICTIONARY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Select a dictionary
 *
 * @param item
 */
export const selectDictionary = item => async dispatch => {
  // TODO: Define action for this in `dictionaryColumns`, use it instead of.
  await dispatch({
    type: dictionaryColsTypes.GET_DICTIONARY_COLUMNS_SUCCESS,
    payload: {
      dictionaryColumns: item.columns,
      count: item.columns.length,
    },
  });

  await dispatch({
    type: types.SELECT_DICTIONARY,
    payload: {
      item,
    },
  });
};

/**
 * Deselect a dictionary
 */
export const deselectDictionary = () => ({
  type: types.DESELECT_DICTIONARY,
});
